<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useAuth } from '@/composables/useAuth';
import { getAssetsUrlForCDN } from '@/utils/template';
import PerfilOficialSwiper from '@/components/perfil-oficial/Swiper.vue';

export type Tournament = {
  custom_data: {};
  description: string;
  duration_ms: number;
  end_time: number;
  hide_leaderboard_min_scores: boolean;
  image1: string;
  image2: string;
  image2_mobile: string;
  instance_id: number;
  is_active: boolean;
  is_can_register: boolean;
  is_cancelled: boolean;
  is_featured: boolean;
  is_finished: boolean;
  is_in_progress: boolean;
  is_upcoming: boolean;
  is_user_registered: boolean;
  min_scores_win: number;
  name: string;
  players_max_count: number;
  players_min_count: number;
  priority: string;
  prize_pool_short: string;
  registration_count: number;
  registration_status: string;
  registration_type: string;
  ribbon: string;
  start_time: number;
  tournament_id: number;
};

export type TournamentInfo = {
  custom_data: {};
  description: string;
  duration_ms: number;
  end_time: number;
  hide_leaderboard_min_scores: boolean;
  image1: string;
  image2: string;
  image2_mobile: string;
  instance_id: number;
  is_active: boolean;
  is_can_register: boolean;
  is_cancelled: boolean;
  is_featured: boolean;
  is_finished: boolean;
  is_in_progress: boolean;
  is_upcoming: boolean;
  is_user_registered: boolean;
  min_scores_win: number;
  name: string;
  players: Array<{
    avatar_url: string;
    is_me: boolean;
    position: number;
    public_username: string;
    scores: number;
  }>;
  players_max_count: number;
  players_min_count: number;
  priority: string;
  prize_pool_short: string;
  prizes: Array<{
    id: number;
    name: string;
    place_from: number;
    place_to: number;
    type: string;
  }>;
  registration_count: number;
  registration_status: string;
  registration_type: string;
  related_games: Array<any>;
  ribbon: string;
  start_time: number;
  tournament_id: number;
};

const props = defineProps<{
  iconPath?: string;
  slug?: string;
  smarticoTournamentFilter?: string | string[];
  isRoletasPage?: boolean;
  accentColor?: string;
}>();

const tournamentGradient = computed(() => {
  if (props.accentColor) {
    return `linear-gradient(90deg, ${props.accentColor} 0%, ${props.accentColor}00 100%)`;
  }

  return props.isRoletasPage
    ? 'linear-gradient(90deg, #f7d038 0%, rgba(247, 208, 56, 0) 100%)'
    : 'linear-gradient(90deg, #ff3400 0%, rgba(255, 52, 0, 0) 100%)';
});

// State
const tournaments = ref<Tournament[]>([]);
const infoByTournamentId = ref<Record<number, TournamentInfo | null>>({});
const remainingTimes = ref<Record<number, string>>({});
const isLoading = ref(true);

const router = useRouter();
const authStore = useAuthStore();
const { openLogin } = useAuth();

function handleButtonClick(tournament: Tournament) {
  if (!authStore.isAuthenticated) {
    openLogin();

    return;
  }

  const status = getTournamentStatus(tournament);

  if (status === 'PARTICIPAR' || status === 'VER RANKING') {
    router.push(`/vip/tournaments/${tournament.instance_id}`);
  }
}

// Methods
async function fetchTournaments(): Promise<Tournament[]> {
  if (typeof window._smartico === 'undefined' || !window._smartico.vapi) {
    console.error('_smartico não está disponível');

    return [];
  }

  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return await window._smartico.api.getTournamentsList();
  } else {
    return await window._smartico.vapi('EN').getTournamentsList();
  }
}

async function loadTournaments() {
  const fetchedTournaments = await fetchTournaments();

  let normalizedFilters: string[] = [];

  if (Array.isArray(props.smarticoTournamentFilter)) {
    normalizedFilters = props.smarticoTournamentFilter
      .filter((f) => typeof f === 'string')
      .map((f: string) => f.toUpperCase());
  } else if (props.smarticoTournamentFilter) {
    normalizedFilters = [String(props.smarticoTournamentFilter).toUpperCase()];
  }

  if (
    normalizedFilters.length === 0 ||
    normalizedFilters.every((filter) => filter === '')
  ) {
    tournaments.value = fetchedTournaments;

    return;
  }

  // Helper function to normalize custom_data
  const normalizeCustomData = (customData: any): string[] => {
    if (!customData) {
      return [];
    }
    if (Array.isArray(customData)) {
      return customData.map(String);
    }

    return String(customData)
      .split(',')
      .map((item) => item.trim());
  };

  if (normalizedFilters.includes('NUMERIC')) {
    tournaments.value = fetchedTournaments.filter(
      (tournament) => getCustomDataNumber(tournament) !== null,
    );
  } else {
    tournaments.value = fetchedTournaments.filter((tournament) => {
      if (!tournament.custom_data) {
        return false;
      }

      const tournamentDataItems = normalizeCustomData(tournament.custom_data);

      if (tournamentDataItems.length === 0) {
        return false;
      }

      return normalizedFilters.some((filter) =>
        tournamentDataItems.some(
          (item) => item.toUpperCase() === filter.toUpperCase(),
        ),
      );
    });
  }
}

async function fetchTournamentInfo(
  tournamentInstanceId: number,
): Promise<TournamentInfo | null> {
  if (typeof window._smartico === 'undefined' || !window._smartico.vapi) {
    console.error('_smartico não está disponível');

    return null;
  }

  return await window._smartico
    .vapi('EN')
    .getTournamentInstanceInfo(tournamentInstanceId);
}

async function loadTournamentInfo(tournamentInstanceId: number) {
  infoByTournamentId.value[tournamentInstanceId] =
    await fetchTournamentInfo(tournamentInstanceId);
}

function formatPlayerName(username: string): string {
  if (username.length <= 4) return username;

  return username.substring(0, 4) + '***';
}

function formatPrize(player: any, tournament: Tournament): string {
  const info = infoByTournamentId.value[tournament.instance_id];

  if (!info || !info.prizes) return 'R$0,00';

  const prize = info.prizes.find(
    (p) => player.position >= p.place_from && player.position <= p.place_to,
  );

  return prize ? prize.name : 'R$0,00';
}

function getTimeRemainingText(tournament: Tournament): string {
  const now = Date.now();
  const endTime = tournament.end_time;
  const remaining = endTime - now;

  if (tournament.start_time > now) {
    return 'INICIA EM';
  }

  return remaining <= 0 ? '' : 'FALTAM';
}

function getTournamentStatus(tournament: Tournament): string {
  const statuses = [
    { condition: tournament.is_user_registered, label: 'VER RANKING' },
    { condition: tournament.is_finished, label: 'FINALIZADO' },
    { condition: !tournament.is_in_progress, label: 'EM BREVE' },
    { condition: true, label: 'PARTICIPAR' },
  ];

  return statuses.find((status) => status.condition)?.label || 'PARTICIPAR';
}

function formatTimeRemaining(end_time: number): string {
  const now = Date.now();
  let diff = end_time - now;

  if (diff <= 0) return '00:00:00:00';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  diff -= days * 1000 * 60 * 60 * 24;

  const hours = Math.floor(diff / (1000 * 60 * 60));

  diff -= hours * 1000 * 60 * 60;

  const minutes = Math.floor(diff / (1000 * 60));

  diff -= minutes * 1000 * 60;

  const seconds = Math.floor(diff / 1000);

  const pad = (n: number) => n.toString().padStart(2, '0');

  return `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

function getCustomDataNumber(tournament: Tournament): number | null {
  if (
    !tournament.custom_data ||
    Object.keys(tournament.custom_data).length === 0
  ) {
    return null;
  }

  const customDataValue = Object.values(tournament.custom_data)[0];

  if (typeof customDataValue === 'string' && !isNaN(Number(customDataValue))) {
    return Number(customDataValue);
  }

  return null;
}

// Lifecycle
onMounted(() => {
  const interval = setInterval(async () => {
    if (window._smartico?.checkInit()) {
      clearInterval(interval);
      isLoading.value = true;

      await loadTournaments();

      tournaments.value.sort((a, b) => {
        const now = Date.now();

        // Ordenação por status (primeiro critério)
        const getStatusWeight = (t: any) => {
          if (t.start_time <= now && t.is_active) return 0; // ativo
          if (t.start_time > now) return 1; // ainda vai começar
          if (t.is_finished) return 2; // finalizado

          return 3; // já começou, mas inativo (não finalizado)
        };

        const statusDiff = getStatusWeight(a) - getStatusWeight(b);

        if (statusDiff !== 0) return statusDiff;

        // Se o status for o mesmo, ordenar por custom_data
        const aCustomData = getCustomDataNumber(a);
        const bCustomData = getCustomDataNumber(b);

        if (aCustomData !== null && bCustomData === null) return -1;
        if (aCustomData === null && bCustomData !== null) return 1;
        if (aCustomData !== null && bCustomData !== null) {
          return aCustomData - bCustomData;
        }

        // Se custom_data também for igual (ou ambos nulos), ordenar por prioridade
        const priorityDiff = Number(a.priority) - Number(b.priority);

        if (priorityDiff !== 0) return priorityDiff;

        // Desempate final por nome alfabético
        return a.name.localeCompare(b.name);
      });

      await Promise.all(
        tournaments.value.map((tournament) =>
          loadTournamentInfo(tournament.instance_id),
        ),
      );

      isLoading.value = false;
    }
  }, 100);

  setInterval(() => {
    tournaments.value.forEach((tournament) => {
      if (tournament.end_time > Date.now()) {
        remainingTimes.value[tournament.instance_id] = formatTimeRemaining(
          tournament.end_time,
        );
      }
    });
  }, 1000);
});
</script>

<template>
  <div
    v-if="!isLoading && tournaments.length > 0"
    :class="$style.tournamentsContainer"
  >
    <PerfilOficialSwiper
      gap="15px"
      :icon-path="props.iconPath || 'home/tournament'"
      :see-more-url="props.slug ? `${props.slug}` : '/vip/tournaments'"
      title="Torneios"
    >
      <template #default="{ handleClick }">
        <div
          v-for="tournament in tournaments"
          :key="tournament.tournament_id"
          class="flex h-[120px] md:h-[210px] min-w-[285px] md:min-w-[425px] ml-[3px]"
          :class="{
            grayscale: !tournament.is_active,
          }"
          @click="handleClick"
        >
          <div class="relative w-[120px] h-[120px] md:w-[210px] md:h-[210px]">
            <!-- Fundo com gradiente centralizado -->
            <div
              class="rounded-md absolute w-[120px] h-[120px] md:w-[210px] md:h-[210px]"
              :style="{
                background: tournamentGradient,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1,
              }"
            ></div>
            <!-- Imagem Torneio -->
            <img
              :alt="`Tournament-${tournament.tournament_id}`"
              class="rounded-md object-cover absolute w-[117px] h-[117px] md:w-[207px] md:h-[207px]"
              :class="{
                grayscale: !tournament.is_in_progress,
              }"
              draggable="false"
              :src="tournament.image2_mobile"
              style="
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                display: block;
                z-index: 2;
              "
            />

            <!-- Sombra linear só embaixo -->
            <div
              class="absolute w-[117px] h-[117px] md:w-[207px] md:h-[207px]"
              style="
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                pointer-events: none;
                border-radius: 0.375rem;
                overflow: hidden;
                z-index: 3;
              "
            >
              <div
                style="
                  position: absolute;
                  bottom: 0;
                  left: 0;
                  width: 100%;
                  height: 60%;
                  pointer-events: none;
                  background: linear-gradient(
                    to top,
                    rgba(0, 0, 0, 1) 0%,
                    rgba(0, 0, 0, 0.6) 75%,
                    rgba(0, 0, 0, 0) 100%
                  );
                  border-radius: 0 0 0.375rem 0.375rem;
                "
              ></div>
            </div>
            <!-- Textos dentro da imagem -->
            <!-- Nome operadora -->
            <div
              class="text-[10px] leading-[12px] w-full text-white absolute top-[1px] z-10 text-center"
            >
              <span
                class="font-bold px-[5px] py-[3px] md:px-[10px] md:py-[6px] justify-center top-0 z-10 text-[8px] md:text-[9px] leading-[10px] rounded-b-lg uppercase"
                style="
                  background: radial-gradient(
                    136.67% 136.67% at 36.76% -16.67%,
                    #ff704a 0%,
                    #ff3400 100%
                  );
                "
                >{{ tournament.ribbon }}</span
              >
            </div>
            <!-- Nome do Torneio e Prêmio -->
            <div
              class="text-[9px] md:text-[12px] leading-[11px] md:leading-[15px] w-full text-white absolute bottom-[10px] md:bottom-[15px] z-10 px-[7px] md:px-[15px] font-semibold"
              style="white-space: normal; overflow-wrap: break-word"
            >
              <span
                v-if="tournament.registration_count"
                class="flex items-center mb-0.5"
              >
                <NuxtIcon
                  class="text-[#00ff00] -mt-0.5 mr-1"
                  name="users"
                />
                {{ tournament.registration_count ?? '---' }}</span
              >
              <span class="font-extrabold md:text-[13px]">{{
                tournament.name
              }}</span
              ><br />
              <span>{{ 'Prêmio: ' + tournament.prize_pool_short }}</span>
            </div>
          </div>

          <div
            class="flex-1 rounded-r-md bg-[linear-gradient(270deg,_rgba(255,255,255,0.05)_0%,_rgba(255,255,255,0)_100%)] flex flex-col p-[5px] pt-0 md:pt-1"
          >
            <table class="text-white text-xs md:text-lg w-full">
              <thead
                :class="{
                  hidden: !tournament.is_in_progress && tournament.is_active,
                }"
              >
                <tr class="text-left text-[8px] md:text-[10px] leading-[10px]">
                  <th class="w-[24px] md:w-[45px] text-center p-[5px] pl-[10px]"
                    >#</th
                  >
                  <th class="px-[5px] w-[75px]">Jogador</th>
                  <th>Prêmio</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(player, playerIndex) in infoByTournamentId[
                    tournament.instance_id
                  ]?.players?.slice(0, 3)"
                  :key="playerIndex"
                  class="text-[8px] md:text-[12px] leading-[10px] md:font-semibold"
                  :class="{
                    'bg-[#D6ED16] text-black': player.is_me,
                    'bg-[#FFFFFF1A]': playerIndex % 2 === 0 && !player.is_me,
                  }"
                >
                  <td
                    class="text-center rounded-l-md h-[22px] md:h-[32px] pl-[5px] w-[24px] md:w-[45px]"
                  >
                    <div class="flex items-center justify-center h-full">
                      <NuxtImg
                        v-if="player.position"
                        :alt="`${player.position}º`"
                        class="w-[24px] md:w-[26px]"
                        draggable="false"
                        :src="
                          getAssetsUrlForCDN(
                            `/assets/gamification/t-place-${player.position}.png`,
                          )
                        "
                      />
                      <span
                        v-else
                        class="text-center text-[8px] md:text-[12px]"
                        >{{ player.position }}</span
                      >
                    </div>
                  </td>
                  <td
                    class="text-[8px] md:text-[12px] leading-[10px] py-[5px] px-[5px] h-[22px] align-middle whitespace-nowrap overflow-hidden text-ellipsis max-w-[50px]"
                    :title="formatPlayerName(player.public_username)"
                    >{{ formatPlayerName(player.public_username) }}</td
                  >
                  <td
                    class="rounded-r-md text-[8px] md:text-[12px] leading-[10px] h-[22px] align-middle whitespace-nowrap overflow-hidden text-ellipsis max-w-0"
                    >{{ formatPrize(player, tournament) }}</td
                  >
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="3">
                    <div
                      class="flex md:flex-wrap items-center pt-[5px] md:pt-[10px] rounded-md gap-2 md:gap-3"
                    >
                      <div
                        class="w-5 h-5 p-[4px] md:w-8 md:h-8 md:p-[7px] self-center rounded-md bg-[#FFFFFF1A]"
                      >
                        <NuxtIcon name="timer" />
                      </div>

                      <div class="max-w-[50px] flex-1 md:flex md:gap-2">
                        <div
                          class="-ml-1 leading-[10px] text-[8px] md:text-[12px] md:opacity-70 md:font-bold"
                          :class="{
                            'md:ml-[30px]': tournament.is_in_progress,
                            'md:ml-0 whitespace-nowrap':
                              !tournament.is_in_progress,
                          }"
                          >{{ getTimeRemainingText(tournament) }}</div
                        >
                        <div
                          class="text-[8px] md:text-[12px] leading-[10px] font-bold -ml-1 tracking-wide w-[60px]"
                          :class="{
                            'text-[#D6ED16]': !tournament.is_in_progress,
                          }"
                          >{{
                            remainingTimes[tournament.instance_id] || '...'
                          }}</div
                        >
                      </div>

                      <button
                        class="p-[5px] md:p-[9px] rounded-md leading-[12px] text-[10px] md:text-[13px] w-full transition-transform duration-300 hover:scale-[1.01] font-extrabold"
                        :class="{
                          'bg-[#0808B3] text-white text-[7px] scale-1 px-0':
                            tournament.is_user_registered,
                          'flex justify-center items-center gap-[7px] border [background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] p-2.5 rounded-md border-solid border-[#FC9984] text-white text-center text-[8px] font-bold leading-[14px]':
                            !tournament.is_user_registered,
                        }"
                        @click="handleButtonClick(tournament)"
                      >
                        {{ getTournamentStatus(tournament) }}
                      </button>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </template>
    </PerfilOficialSwiper>
  </div>
</template>

<style module lang="postcss">
.tournamentsContainer {
  @apply mb-4 md:mb-8;
}

.navigationButton {
  @apply hover:text-primary cursor-pointer text-texts/60;
  user-select: none;

  & + .navigationButton {
    @apply ml-3;
  }

  &.isNavigationButtonDisabled {
    @apply text-texts/20 cursor-not-allowed;
  }
}

.title-torneios {
  user-select: none;
}

.titleText {
  user-select: none;
}

/* Apply to the entire header container to prevent selection on all child elements */
.title-torneios > * {
  user-select: none;
}
</style>
